import type { FormInstance } from "antd"
import type { Dayjs } from "dayjs"
import { useCallback, useMemo, useRef } from "react"

import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import {
  MILESTONE_SCHEDULE_FIELDS,
  type MilestoneScheduleField,
  type MilestoneScheduleFields,
  resolveMilestoneSchedule
} from "../../../../utils/milestone-effort.ts"

/** What a milestone form's submit handler hands back once the save settled. */
export interface MilestoneSaveResult {
  ok: boolean
  fieldErrors?: Partial<Record<MilestoneScheduleField, string[]>>
}

type NormalizeDate = (
  value: string | Dayjs | Date | null | undefined
) => string | null

/**
 * Planned Start, Planned End and Effort in one milestone form: each edit
 * previews the field it leaves out ("last edit wins", the server's rule), and
 * the save sends only the schedule fields the user typed into, so the server
 * derives the rest and never sees a stale value beside a new one.
 */
export const useMilestoneScheduleFields = (
  form: FormInstance<ProjectMilestone>,
  normalizeDate: NormalizeDate
) => {
  const edited = useRef(new Set<MilestoneScheduleField>())

  const onEdit = useCallback(
    (
      field: MilestoneScheduleField,
      value: Dayjs | string | number | null | undefined
    ) => {
      edited.current.add(field)
      const sent =
        field === "duration_days"
          ? ((value as number | null | undefined) ?? null)
          : normalizeDate(value as Dayjs | string | null | undefined)
      const current: MilestoneScheduleFields = {
        start_date: normalizeDate(form.getFieldValue("start_date")),
        end_date: normalizeDate(form.getFieldValue("end_date")),
        duration_days: form.getFieldValue("duration_days") ?? null
      }
      const resolved = resolveMilestoneSchedule(current, { [field]: sent })
      const derived = MILESTONE_SCHEDULE_FIELDS.filter(
        (other) => other !== field && resolved[other] !== current[other]
      )
      if (!derived.length) return
      form.setFieldsValue(
        Object.fromEntries(
          derived.map((other) => [other, resolved[other]])
        ) as Partial<ProjectMilestone>
      )
      void form
        .validateFields(["start_date", "end_date"], { recursive: false })
        .catch(() => undefined)
    },
    [form, normalizeDate]
  )

  const withEditedScheduleOnly = useCallback(
    (values: Partial<ProjectMilestone>): Partial<ProjectMilestone> => {
      const {
        start_date: startDate,
        end_date: endDate,
        duration_days: durationDays,
        ...rest
      } = values
      const typed: MilestoneScheduleFields = {
        start_date: normalizeDate(startDate),
        end_date: normalizeDate(endDate),
        duration_days: durationDays ?? null
      }
      return {
        ...rest,
        ...Object.fromEntries(
          [...edited.current].map((field) => [field, typed[field]])
        )
      }
    },
    [normalizeDate]
  )

  const showServerErrors = useCallback(
    (result: MilestoneSaveResult) => {
      const entries = Object.entries(result.fieldErrors ?? {})
      if (!entries.length) return
      form.setFields(
        entries.map(([name, errors]) => ({
          name: name as keyof ProjectMilestone,
          errors
        }))
      )
    },
    [form]
  )

  return useMemo(
    () => ({ onEdit, withEditedScheduleOnly, showServerErrors }),
    [onEdit, withEditedScheduleOnly, showServerErrors]
  )
}
